import { DeleteOrderDialog } from '@/components/DeleteOrderDialog';
import { StatCard } from '@/components/StatCard';
import { Skeleton } from '@/components/ui/skeleton';
import { useOrders } from '@/hooks/useOrder';
import { useCurrentOrganization } from '@/hooks/useOrganization';
import { getUserRole } from '@/lib/auth/token';
import type { Order } from '@octave/contracts';
import { Clock, Package } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DataTable } from './orders/data-table';

export function OrdersView() {
  const { data: organization } = useCurrentOrganization();
  const { data: orders, isLoading } = useOrders(organization?.id);
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilters = useMemo(() => searchParams.getAll('status'), [searchParams]);
  const isOctaveAdmin = getUserRole() === 'ADMIN';
  const [orderToDelete, setOrderToDelete] = useState<Order | null>(null);

  const handleStatusFiltersChange = (next: string[]) => {
    const params = new URLSearchParams(searchParams);
    params.delete('status');
    next.forEach((v) => params.append('status', v));
    setSearchParams(params, { replace: true });
  };

  const stats = useMemo(() => {
    if (!orders) return null;

    const pendingCount = orders.filter(
      (o) => o.status.toLowerCase() === 'pending'
    ).length;
    const completedCount = orders.filter(
      (o) => o.status.toLowerCase() === 'completed'
    ).length;
    const cancelledCount = orders.filter(
      (o) => o.status.toLowerCase() === 'cancelled'
    ).length;

    return {
      total: orders.length,
      pendingCount,
      completedCount,
      cancelledCount,
    };
  }, [orders]);

  const TableSkeleton = () => (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-32 rounded-2xl" />
        ))}
      </div>
      <div className="flex items-center gap-4 pb-4">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-8 w-32" />
      </div>
      {[...Array(10)].map((_, i) => (
        <div key={i} className="flex gap-2">
          <Skeleton className="h-16 w-full rounded-xl" />
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Mes Commandes</h1>
        <p className="text-sm text-slate-500 mt-1">
          Consultez l'historique de vos commandes
        </p>
      </div>

      {isLoading ? (
        <TableSkeleton />
      ) : (
        <>
          {stats && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard
                icon={Package}
                iconBg="bg-emerald-50"
                iconColor="text-emerald-600"
                label="Total commandes"
                value={stats.total}
              >
                <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
                  <span>En cours : {stats.pendingCount}</span>
                  <span>Terminées : {stats.completedCount}</span>
                  <span>Annulées : {stats.cancelledCount}</span>
                </div>
              </StatCard>

              <StatCard
                icon={Clock}
                iconBg="bg-amber-50"
                iconColor="text-amber-600"
                label="En cours"
                value={stats.pendingCount}
              >
                <p className="mt-3 text-xs text-slate-500">
                  Commandes en attente
                </p>
              </StatCard>

            </div>
          )}

          <div>
            <DataTable
              data={orders || []}
              statusFilters={statusFilters}
              onStatusFiltersChange={handleStatusFiltersChange}
              onDelete={isOctaveAdmin ? setOrderToDelete : undefined}
            />
          </div>
        </>
      )}

      <DeleteOrderDialog
        order={orderToDelete}
        onClose={() => setOrderToDelete(null)}
      />
    </div>
  );
}
